import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { App, type MenuProps } from 'antd';
import { EuroCircleOutlined, ExclamationCircleOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { useSetBlockClient } from 'api/clients/use-set-block-client.ts';
import { UserRole, withRoleGuard } from 'shared/hocs/with-role-guard.tsx';
import { MoreActionsDropdown } from 'components/common/more-actions-dropdown';
import { UpdateCreditLimitModal } from './components/update-credit-limit-modal';
import type { IClientExtraActionsProps } from './types';

export const ClientExtraActionsComponent: React.FC<IClientExtraActionsProps> = (props) => {
  const { clientId, isBlocked, creditLimit } = props;
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const [isCreditLimitModalOpen, setIsCreditLimitModalOpen] = useState(false);

  const { mutateAsync: setBlockClient } = useSetBlockClient(clientId);

  const actionLabel = isBlocked ? t('client:unblock_client') : t('client:block_client');
  const confirmText = isBlocked ? t('client:unblock_client_confirm_text') : t('client:block_client_confirm_text');

  const handleOpenCreditLimitModal = () => setIsCreditLimitModalOpen(true);
  const handleCloseCreditLimitModal = () => setIsCreditLimitModalOpen(false);

  const confirmBlockChange = () => {
    modal.confirm({
      title: t('common:confirm'),
      icon: <ExclamationCircleOutlined />,
      content: confirmText,
      okText: actionLabel,
      cancelText: t('common:cancel'),
      onOk: async () => {
        await setBlockClient({ isBlocked: !isBlocked });
      },
      maskClosable: true,
    });
  };

  const handleDropdownClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'block') {
      confirmBlockChange();
    }

    if (key === 'credit-limit') {
      handleOpenCreditLimitModal();
    }
  };

  return (
    <>
      <MoreActionsDropdown
        items={[
          {
            key: 'credit-limit',
            label: t('client:update_credit_limit'),
            icon: <EuroCircleOutlined />,
          },
          {
            key: 'block',
            label: actionLabel,
            icon: isBlocked ? <UnlockOutlined /> : <LockOutlined />,
          },
        ]}
        onClick={handleDropdownClick}
      />
      <UpdateCreditLimitModal
        clientId={clientId}
        creditLimit={creditLimit}
        isOpen={isCreditLimitModalOpen}
        onClose={handleCloseCreditLimitModal}
      />
    </>
  );
};

export const ClientExtraActions = withRoleGuard(ClientExtraActionsComponent, [UserRole.admin]);
